import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { finalize } from 'rxjs/operators';
import { PartnerCompaniesComponent } from '@features/dashboard/partner/components/partner-companies/partner-companies.component';
import { PartnerContractsComponent } from '@features/dashboard/partner/components/partner-contracts/partner-contracts.component';
import { PartnerReferralsComponent } from '@features/dashboard/partner/components/partner-referrals/partner-referrals.component';
import { Permissions } from '@app/core/guard/Permissions';
import { PermissionsService } from '@services/helpers/permissions.service';

/**
 * Cadastro e edição de parceiro (card 2916). Menu: Negociação › Comercial › Parceiros desde o
 * card 3683; antes ficava em Gerenciamento › Parceiros.
 *
 * A mesma tela atende os dois casos, como em Empresas: sem `:id` é cadastro, com `:id` é
 * edição. São os mesmos três campos do auto-cadastro da landing — decisão do Breno em
 * 15/09/2026 —, e o que muda entre os dois caminhos não é o formulário, e sim o convite:
 * aqui o parceiro recebe um **link** para a tela de criar senha, onde aceita os termos.
 */
@Component({
  selector: 'app-partner-register',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    PartnerCompaniesComponent,
    PartnerContractsComponent,
    PartnerReferralsComponent,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
  ],
  templateUrl: './partner-register.component.html',
  styleUrls: ['./partner-register.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerRegisterComponent implements OnInit {
  readonly home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard/dashboard-management' };
  readonly breadcrumbs: MenuItem[] = [
    { label: 'Comercial' },
    { label: 'Parceiros', routerLink: '/dashboard/partner/list' },
  ];

  readonly form: FormGroup;
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly partnerId = signal<string | null>(null);

  /**
   * Aba ativa (0 = Dados, 1 = Empresas, 2 = Contratos, 3 = Indicações). As abas só existem em
   * modo edição.
   */
  readonly abaAtiva = signal(0);

  /** Aba "Contratos" (card 3462): só com a permissão de listar contratos de parceria. */
  readonly canListContracts = toSignal(
    inject(PermissionsService).hasPermission(Permissions.PartnerContract.LIST),
    { initialValue: false }
  );

  /**
   * Aba "Indicações" (Indique e ganhe, card 3461): só com `PARTNER_VIEW_REFERRALS`, a mesma
   * permissão que o `GET partners/{id}/referrals` confere.
   */
  readonly canViewReferrals = signal(false);

  constructor(
    private readonly fb: FormBuilder,
    private readonly partnersService: PartnersHttpService,
    private readonly toast: ToastHelperService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly destroyRef: DestroyRef,
    private readonly permissionsService: PermissionsService
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');

    if (id) {
      this.partnerId.set(id);
      this.carregar(id);

      // `?tab=contracts`: a volta do editor de contrato (card 3462) cai direto na aba Contratos.
      const tab = this.route.snapshot.queryParamMap.get('tab');
      if (tab === 'contracts') {
        this.abaAtiva.set(2);
      }
      // `?tab=referrals`: o sino de cadastro por indicação (card 3683) abre direto na aba
      // Indicações. Sem `PARTNER_VIEW_REFERRALS` a aba nem existe e o p-tabs fica sem painel; o
      // aviso só chega a quem tem essa permissão.
      if (tab === 'referrals') {
        this.abaAtiva.set(3);
      }
    }

    this.permissionsService
      .hasPermission(Permissions.Partner.VIEW_REFERRALS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(canView => this.canViewReferrals.set(canView));
  }

  /** Título e rótulo do botão dependem de estarmos criando ou editando. */
  get editando(): boolean {
    return this.partnerId() !== null;
  }

  /** Troca de aba do `p-tabs`. O valor só é numérico nas abas que existem aqui. */
  mudarAba(valor: number | string | undefined): void {
    if (typeof valor === 'number') {
      this.abaAtiva.set(valor);
    }
  }

  /** Grava o parceiro — cadastro novo ou edição, conforme a rota. */
  salvar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = {
      name: (this.form.value.name ?? '').trim(),
      email: (this.form.value.email ?? '').trim(),
      phoneNumber: (this.form.value.phoneNumber ?? '').trim(),
    };

    this.saving.set(true);

    const id = this.partnerId();
    const requisicao = id
      ? this.partnersService.updatePartner(id, payload)
      : this.partnersService.createPartner(payload);

    requisicao
      .pipe(
        finalize(() => this.saving.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.toast.showToastSuccess(
            id ? 'Parceiro atualizado' : 'Parceiro cadastrado',
            // O convite é a parte que surpreende quem cadastra: o parceiro não vira
            // usuário ativo na hora, ele precisa criar a senha pelo link.
            id ? '' : 'Enviamos um convite por e-mail para ele criar a senha de acesso.'
          );
          this.voltar();
        },
        error: erro =>
          this.toast.showToastError(
            id ? 'Não foi possível salvar o parceiro' : 'Não foi possível cadastrar o parceiro',
            erro?.errors?.length ? erro.errors[0].message : ''
          ),
      });
  }

  /** Volta para a listagem. */
  voltar(): void {
    this.router.navigate(['/dashboard/partner/list']);
  }

  /** Carrega o parceiro que está sendo editado. */
  private carregar(id: string): void {
    this.loading.set(true);

    this.partnersService
      .getPartnerById(id)
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: parceiro => {
          if (!parceiro) {
            this.toast.showToastError('Parceiro não encontrado', '');
            this.voltar();
            return;
          }

          this.form.patchValue({
            name: parceiro.name,
            email: parceiro.email,
            phoneNumber: parceiro.phoneNumber,
          });
        },
        error: erro =>
          this.toast.showToastError(
            'Não foi possível carregar o parceiro',
            erro?.errors?.length ? erro.errors[0].message : ''
          ),
      });
  }
}
